import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryImages: Record<string, string> = {
  "Shoes": "https://readdy.ai/api/search-image?query=Single%20minimalist%20white%20sneaker%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-shoes-100&orientation=squarish",
  "Hoodies/Sweaters": "https://readdy.ai/api/search-image?query=Single%20gray%20hoodie%20folded%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-hoodie-101&orientation=squarish",
  "T-Shirts": "https://readdy.ai/api/search-image?query=Single%20white%20cotton%20t-shirt%20folded%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-tshirt-102&orientation=squarish",
  "Jackets": "https://readdy.ai/api/search-image?query=Single%20olive%20green%20jacket%20folded%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-jacket-103&orientation=squarish",
  "Pants/Shorts": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20tailored%20beige%20pants%20folded%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-pants-104&orientation=squarish",
  "Headwear": "https://readdy.ai/api/search-image?query=Single%20stylish%20baseball%20cap%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-headwear-105&orientation=squarish",
  "Sets": "https://readdy.ai/api/search-image?query=Single%20matching%20hoodie%20and%20sweatpants%20set%20folded%20together%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-sets-106&orientation=squarish",
  "Underwear/Underpants": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20folded%20underwear%20neatly%20arranged%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-underwear-107&orientation=squarish",
  "Jersey": "https://readdy.ai/api/search-image?query=Single%20sports%20jersey%20folded%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-jersey-108&orientation=squarish",
  "Accessories": "https://readdy.ai/api/search-image?query=Single%20minimalist%20watch%20and%20bracelet%20accessory%20set%20arranged%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-accessories-109&orientation=squarish",
  "Bags": "https://readdy.ai/api/search-image?query=Single%20structured%20leather%20tote%20bag%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-bags-110&orientation=squarish",
  "Belts": "https://readdy.ai/api/search-image?query=Single%20premium%20leather%20belt%20coiled%20neatly%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-belts-111&orientation=squarish",
  "Perfumes": "https://readdy.ai/api/search-image?query=Single%20elegant%20perfume%20bottle%20isolated%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-perfumes-112&orientation=squarish",
  "Other Stuff": "https://readdy.ai/api/search-image?query=Single%20stylish%20sunglasses%20and%20keychain%20set%20arranged%20on%20clean%20light%20background%20tiny%20product%20thumbnail%20minimal%20studio%20photography&width=80&height=80&seq=nav-cat-other-113&orientation=squarish",
};

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-baseline gap-1 whitespace-nowrap cursor-pointer"
          >
            <span className="text-sm font-bold tracking-widest text-foreground-900 font-heading">
              CSSBUY
            </span>
            <span className="hidden sm:inline text-xs font-light tracking-[0.3em] text-foreground-600 font-heading">
              SPREADSHEETS
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                isHome ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
              }`}
            >
              {t("nav.home")}
            </Link>

            {/* Category Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>{t("nav.select_category")}</span>
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-72 bg-background-50 border border-background-200/70 rounded-2xl py-3 max-h-96 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="font-medium">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/guia-cssbuy-sheet"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("nav.guides")}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("nav.safety")}
            </a>
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold border border-foreground-300 text-foreground-700 rounded-full hover:bg-foreground-50 hover:border-foreground-500 transition-all whitespace-nowrap cursor-pointer"
            >
              {t("nav.shop_guide")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              cssbuy sheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
            {/* Home */}
            <Link
              to="/"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-home-4-line text-lg"></i>
              </div>
              {t("nav.home")}
            </Link>

            {/* Shopping Guide */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-shopping-bag-3-line text-lg"></i>
              </div>
              {t("nav.shop_guide")}
            </a>

            {/* Guide */}
            <Link
              to="/guia-cssbuy-sheet"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <i className="ri-book-open-line text-lg"></i>
              </div>
              {t("nav.guides")}
            </Link>

            {/* Safety */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <i className="ri-shield-check-line text-lg"></i>
              </div>
              {t("nav.safety")}
            </a>

            {/* Mobile Categories Toggle */}
            <div className="border-t border-background-200/70 mt-1 pt-3">
              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-800 cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600">
                    <i className="ri-grid-line text-lg"></i>
                  </div>
                  {t("nav.select_category")} ({categories.length})
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileCatOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isMobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 mt-2 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 p-2.5 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile CTA */}
            <div className="border-t border-background-200/70 pt-3 mt-1">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all cursor-pointer"
              >
                cssbuy sheet
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}